import { FC, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { clsx } from 'clsx'

import { RoutingPaths } from 'app/routes/routes.ts'

import { calendarTabs } from 'pages/tours/tours-calendar-page/model.ts'

import { DateAndNights } from 'features/tours/ui/date-and-nights'

import { getButtonText } from 'entities/tour/libs/getNightsDateText.ts'
import searchTour from 'entities/tour/models/search-tour/search-tour.ts'

import { flexibleDateRange } from 'shared/constants/tours'
import { useCalendarFocus, useDateRangeSelection } from 'shared/hooks'
import {
  formatDateFetch,
  prepareCalendarPrices,
} from 'shared/lib/calendarDateUtils'
import { CalendarHeader } from 'shared/ui/calendar-header'
import { FlexibleDepartureCell } from 'shared/ui/flexible-departure-cell'
import { MonthWrapper } from 'shared/ui/month-wrapper'

import { ButtonCalendar } from './ components'
import { Props, TYPE_DATE } from './model'
import styles from './styles.module.scss'

export const DateRangePicker: FC<Props> = ({
  header,
  difficult,
  direction,
  focusDate,
  showRange,
  activeDays,
  selectedDate,
  selectedDates,
  indexDirection,
  calendarPrices,
  placeholders,
  isFlexibleDeparture = false,
  triggerCalendar,
  onFlexibleDate,
  onSubmit,
  maxMonth,
  nightsText,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const stickyHeaderRef = useRef<HTMLDivElement>(null)

  const { selected, handleDateClick, resetValue } = useDateRangeSelection({
    initialSelectedDate: selectedDate,
    difficult,
    activeDays,
    triggerCalendar,
    direction,
  })

  useCalendarFocus(focusDate, { headerRef: stickyHeaderRef })

  const onResetValue = (date: TYPE_DATE) => {
    resetValue(date)
  }

  const handleSubmit = onSubmit ?? (() => navigate(RoutingPaths.tours.path()))

  const handleDateChange = (date: string) => {
    handleDateClick(date)

    searchTour.setStartFlight({
      startDay: formatDateFetch(date) as string,
    })
  }

  const tabsHandler = (value: string) => {
    navigate(`${RoutingPaths.tours.path()}${value}`, { replace: true })
  }

  const dateListPrice = prepareCalendarPrices(calendarPrices)
  const selectedDatesList = difficult ? selectedDates : undefined

  const dateText = getButtonText(selected, isFlexibleDeparture)

  return (
    <div className={clsx('container', styles.container)}>
      {header}
      <div className={styles.range}>
        {showRange && (
          <CalendarHeader
            selected={selected}
            placeholders={placeholders}
            onResetValue={onResetValue}
          />
        )}
      </div>

      <div
        ref={stickyHeaderRef}
        className={styles.tabsWrapper}
      >
        <DateAndNights
          nightsText={nightsText}
          dateText={dateText}
          onClick={tabsHandler}
          defaultActive={calendarTabs[0].path}
        />

        <FlexibleDepartureCell
          title={t('tours_block.flexible_departure', {
            days: flexibleDateRange,
          })}
          isEnabled={isFlexibleDeparture}
          onToggle={onFlexibleDate}
        />
      </div>

      <div className={styles.calendar}>
        <MonthWrapper
          indexDirection={indexDirection}
          activeDays={activeDays}
          isDifficult={difficult}
          dateInfo={dateListPrice}
          selected={selected || null}
          selectedDays={selectedDatesList}
          onClick={handleDateChange}
          isFlexibleDeparture={isFlexibleDeparture}
          maxMonth={maxMonth}
        />
      </div>

      <ButtonCalendar
        onClick={handleSubmit}
        disabled={!selectedDate?.startDate}
      />
    </div>
  )
}
